import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SectorMenu from './SectorMenu';
import Home from './Home';
import Pcpn from './Pcpn';
import AcisPcpnMaps from './AcisPcpnMaps';
import UsgsGroundwater from './UsgsGroundwater';
import UsgsStreamflow from './UsgsStreamflow';
import Usdm from './Usdm';
import Sport from './Sport';
import Grace from './Grace';
import Eddi from './Eddi';
import ChamplainGraph from './ChamplainGraph';
import DsciGraph from './DsciGraph';
import BasicMap from './BasicMap';
import Publications from './Publications';

const drawerWidth = 220;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: 0,
        },
      },
    ],
  }),
);

const AppBar = styled(MuiAppBar, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    variants: [
      {
        props: ({ open }) => open,
        style: {
          width: `calc(100% - ${drawerWidth}px)`,
          marginLeft: `${drawerWidth}px`,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      },
    ],
  })
);

const DrawerHeader = styled('div')(
  ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  })
);

//------------------------------
//------------------------------

export default function PersistentDrawerInterface(props) {
  const { mode } = props;
  const [ open, setOpen ] = useState(true);
  const [ spiSaved, setSpiSaved ] = useState({markers:null, date:null, mapspi:"30-day", showdm:false});
  const [ gwSaved, setGwSaved ] = useState({markers:null, date:null, showdm:false});
  const [ sfSaved, setSfSaved ] = useState({markers:null, date:null, period:"7-Day", showdm:false});
  const [ searchParams ] = useSearchParams();
  const [ productParam, setProductParam ] = useState("home");

  const NotFound = () => {
    // catch all for route not defined
    return (
      <div>
        <Typography variant="h6">404 - Page Not Found</Typography>
        <Typography>The page you are looking for does not exist.</Typography>
      </div>
    );
  };

  const ToIframe = ({ iframeSource }) => {
    // open page in an iframe
    return (
      <div style={{ width: '100%', height: '100vh' }}>
        <iframe 
          src = {iframeSource} 
          title = {"External Content from " + iframeSource}
          style = {{ border: 'none', width: '100%', height: '100%' }} 
        />
      </div>
    );
  };

  const ExternalLinkButton = ({ url, text }) => {
    // notify new window opened and add button to reopen external link in new tab
    const handleButtonClick = (url) => {
        window.open(url, "_blank", "noopener, noreferrer");
    };
    return (
      <>
        <Typography
          variant='body1'
          sx={{ my: 2 }}
        >
          &ldquo;{text}&rdquo; was opened in a new tab.
        </Typography>
        <Button 
          variant="contained"
          size="small"
          sx={{ textTransform: 'none' }}
          onClick={() => { handleButtonClick(url) }}
        >
          Click to reopen
        </Button>
        </>
    );
  };

  const toggleDrawerOpen = () => {
    // toggle status of navigation panel (drawer)
    setOpen(!open);
  };
  
  useEffect(() => {
    // open display panel if "product" query parameter is present in URL
    const productParam = searchParams.get("product") || "home";
    setProductParam(productParam);
  }, [searchParams]);


  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawerOpen}
            edge="start"
            sx={[{ mr: 2 }, open && { display: 'none' }]}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h4" 
            sx={{ fontWeight:600, textAlign:"center", width:"100%" }}
          >
            Vermont Drought Dashboard
          </Typography>
        </Toolbar>
      </AppBar>

      <nav>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <DrawerHeader>
            <IconButton onClick={toggleDrawerOpen}>
              <ChevronLeftIcon />
            </IconButton>
          </DrawerHeader>
          <Divider />
          <Link to={"?product=home"}>
            <Button variant="text" size="large" sx={{ mt:2 }}>Home</Button>
          </Link>
          <SectorMenu />
          <Link to={"?product=pubs"}>
            <Button variant="text" size="large">Publications</Button>
          </Link>
        </Drawer>
      </nav>

      <Main open={open}>
        <DrawerHeader />
        {productParam === 'home' && <Home />}
        {productParam === 'pcpn' && <Pcpn 
              spiSaved={spiSaved}
              setSpiSaved={setSpiSaved}
              mode={mode}
            />}
        {productParam === 'acispcpn' && <AcisPcpnMaps />}
        {productParam === 'sport' && <Sport />}
        {productParam === 'grace' && <Grace />}
        {productParam === 'eddi' && <Eddi />}
        {productParam === 'agsurvey' && <ExternalLinkButton
              url="https://agriculture.vermont.gov/drought"
              text="Agriculture Drought Impact Survey (VAAFM)" 
            />}
        {productParam === 'sf' && <UsgsStreamflow 
              sfSaved={sfSaved}
              setSfSaved={setSfSaved}
              mode={mode}
            />}
        {productParam === 'gw' && <UsgsGroundwater 
              gwSaved={gwSaved}
              setGwSaved={setGwSaved}
              mode={mode}
            />}
        {productParam === 'champlain' && <ChamplainGraph />}
        {productParam === 'nwssnow' && <ExternalLinkButton 
              url="https://www.weather.gov/nerfc/snow" 
              text="Snow Information Page (NWS/NERFC)"
            />}
        {productParam === 'vtdec' && <ExternalLinkButton 
              url="https://experience.arcgis.com/experience/be35cd1bf2c5407b9576f946f5b2f453" 
              text="Vermont Drought Reports (VT DEC)"
            />}
        {productParam === 'vtpws' && <ExternalLinkButton 
              url="https://experience.arcgis.com/experience/1ca6197412c64163937d7fb7cc220582"
              text="Vermont Public Water System Drought Reports (VT DEC)"
            />}    
        {productParam === 'vtdoh' && <ExternalLinkButton 
              url="https://www.healthvermont.gov/environment/drinking-water/drought-and-your-well" 
              text="Drought and Your Well (VT DOH)" 
            />}
       {productParam === 'kbdi' && <BasicMap
              mapurl="https://nedews.nrcc.cornell.edu/NRCC_maps/kbdi_vt_current.png" 
              mapTitle="Keetch-Byram Drought Index Status"
            />}
        {productParam === 'firedanger' && <BasicMap 
              mapurl="https://anrmaps.vermont.gov/websites/firedanger/VermontFireDanger.jpg?_gl=1*1a5102e*_ga*MTcxODk1OTUxNi4xNzU2NDgyMDc3*_ga_V9WQH77KLW*czE3NTc1MTAyOTkkbzUkZzEkdDE3NTc1MTE2NzAkajExJGwwJGgw"
              mapTitle="Fire Danger Forecast"
              foottext="Visit <a href='https://fpr.vermont.gov/forest/wildland-fire/monitoring-fire-danger'>https://fpr.vermont.gov/forest/wildland-fire/monitoring-fire-danger</a> for more information."
            />}
        {productParam === 'fireea' && <ExternalLinkButton
              url="https://storymaps.arcgis.com/stories/392023524a5a4c20a237237b5f935eb0"
              text="Eastern Area Fire Environmental Outlook" 
            />}
        {productParam === 'cdc' && <ExternalLinkButton
              url="https://www.cdc.gov/drought-health/about/" 
              text="Drought and Your Health (CDC)" 
            />}
        {productParam === 'airnow' && <ExternalLinkButton
              url="https://www.airnow.gov/state/?name=vermont" 
              text="AirNow Air Quality Index" 
            />}
        {productParam === 'cyanbac' && <ExternalLinkButton
              url="https://www.healthvermont.gov/environment/tracking/cyanobacteria-blue-green-algae-tracker"
              text="Cyanobacteria Tracker" 
            />}
        {productParam === 'cmor' && <ExternalLinkButton 
              url="https://droughtimpacts.unl.edu/Tools/ConditionMonitoringObservations.aspx"
              text="Condition Monitoring Observer Reports"
            />}
        {productParam === 'dir' && <ExternalLinkButton 
              url="https://unldroughtcenter.maps.arcgis.com/apps/dashboards/46afe627bb60422f85944d70069c09cf"
              text="Drought Impact Reporter Dashboard (NDMC)"
            />}
        {productParam === 'cocoreps' && <ExternalLinkButton 
              url="https://www.cocorahs.org/ViewData/ListConditionMonitoringReports.aspx"
              text="CoCoRaHS Condition Monitoring Reports"
            />}
        {productParam === 'usdm' && <Usdm 
              mappng="data/png/current/current_vt_trd.png"
            />}
        {productParam === 'usdmchng1' && <Usdm 
              mappng="data/chng/png/current/current_VT_chng_1W.png"
            />}
        {productParam === 'usdmchng2' && <Usdm 
              mappng="data/chng/png/current/current_VT_chng_2W.png"
            />}
        {productParam === 'usdmchng4' && <Usdm 
              mappng="data/chng/png/current/current_VT_chng_4W.png"
            />}
        {productParam === 'usdmdsci' && <DsciGraph />}
        {productParam === 'usdmabout' && <ToIframe 
              iframeSource="https://droughtmonitor.unl.edu/About/AbouttheData/DroughtClassification.aspx" 
            />}
        {productParam === 'nceiterm' && <ExternalLinkButton
              url="https://www.ncei.noaa.gov/access/monitoring/drought-recovery/current/wmp/end-rain/1"
              text="Current Drought Reduction (NCEI)" 
            />}
        {productParam === 'pubs' && <Publications />}
        {!productParam.startsWith('usdm') && !['home','eddi','pcpn','acispcpn','sport','grace','gw','sf','champlain','nwssnow','usdmabout','vtdec','vtpws','vtdoh','cmor','dir','cocoreps','cdc','airnow','cyanbac','nceiterm','fireea','agsurvey','pubs','kbdi','firedanger'].includes(productParam) && <NotFound />} 

     </Main>
    </Box>
  );
}